import {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {
  CheckCircle2,
  Clock3,
  FileCheck2,
  History,
  Loader2,
  Search,
  SlidersHorizontal,
  Trash2,
  Undo2,
  X,
} from 'lucide-react'
import {
  AdjustmentActorType,
  ModifiedPurchaseOrderHistory,
  SavedOrderAuditAction,
  SavedPurchaseOrderAudit,
} from '../services/purchaseOrdersApi'

type ChangeFilter = 'all' | 'quantity_changed' | 'removed' | 'restored' | 'lifecycle'
type ActorFilter = 'all' | AdjustmentActorType

interface PurchaseOrderHistoryModalProps {
  open: boolean
  onClose: () => void
  groups: ModifiedPurchaseOrderHistory[]
  loading?: boolean
  error?: string
  title?: string
  subtitle?: string
}

interface FlatHistoryRow {
  order: ModifiedPurchaseOrderHistory
  change: SavedPurchaseOrderAudit
}

function formatCnpj(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 14) return value
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

function formatDate(value?: string | null) {
  if (!value) return '\u2014'
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value))
}

function actionMeta(action: SavedOrderAuditAction) {
  switch (action) {
    case 'quantity_changed':
      return {label: 'Quantidade alterada', className: 'bg-blue-50 text-blue-700 ring-blue-100', icon: SlidersHorizontal}
    case 'removed':
      return {label: 'Item removido', className: 'bg-red-50 text-red-700 ring-red-100', icon: Trash2}
    case 'restored':
      return {label: 'Item restaurado', className: 'bg-amber-50 text-amber-700 ring-amber-100', icon: Undo2}
    case 'created':
      return {label: 'Pr\u00e9-pedido criado', className: 'bg-teal-50 text-teal-700 ring-teal-100', icon: CheckCircle2}
    case 'finalized':
      return {label: 'Pedido finalizado', className: 'bg-emerald-50 text-emerald-700 ring-emerald-100', icon: FileCheck2}
    default:
      return {label: 'Pedido atualizado', className: 'bg-gray-100 text-gray-700 ring-gray-200', icon: History}
  }
}

function valueBefore(change: SavedPurchaseOrderAudit) {
  if (change.action === 'removed') {
    const quantity = change.final_quantity || change.suggested_quantity
    return quantity ? `${quantity} un no pedido` : 'Item no pedido'
  }
  if (change.action === 'restored') return 'Removido'
  if (change.action === 'created') return 'N\u00e3o existente'
  if (change.action === 'finalized') return 'Pr\u00e9-pedido'
  return change.old_value ?? '\u2014'
}

function valueAfter(change: SavedPurchaseOrderAudit) {
  if (change.action === 'removed') return 'Removido'
  if (change.action === 'restored') {
    const quantity = change.final_quantity || change.suggested_quantity
    return quantity ? `Restaurado com ${quantity} un` : 'Restaurado'
  }
  if (change.action === 'created') return 'Pr\u00e9-pedido'
  if (change.action === 'finalized') return 'Pedido finalizado'
  return change.new_value ?? '\u2014'
}

function matchesChangeFilter(action: SavedOrderAuditAction, filter: ChangeFilter) {
  if (filter === 'all') return true
  if (filter === 'lifecycle') return action === 'created' || action === 'finalized'
  return action === filter
}

export function PurchaseOrderHistoryModal({
  open,
  onClose,
  groups,
  loading = false,
  error = '',
  title = 'Hist\u00f3rico de altera\u00e7\u00f5es',
  subtitle = 'Veja o que foi alterado, por quem, quando e por qual motivo.',
}: PurchaseOrderHistoryModalProps) {
  const [search, setSearch] = useState('')
  const [orderFilter, setOrderFilter] = useState('all')
  const [changeFilter, setChangeFilter] = useState<ChangeFilter>('all')
  const [actorFilter, setActorFilter] = useState<ActorFilter>('all')

  useEffect(() => {
    if (!open) return
    setSearch('')
    setOrderFilter('all')
    setChangeFilter('all')
    setActorFilter('all')
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open, onClose])

  const allRows = useMemo<FlatHistoryRow[]>(
    () => groups.flatMap((order) => order.changes.map((change) => ({order, change}))),
    [groups]
  )

  const summary = useMemo(() => ({
    orders: groups.length,
    quantities: allRows.filter(({change}) => change.action === 'quantity_changed').length,
    removed: allRows.filter(({change}) => change.action === 'removed').length,
    restored: allRows.filter(({change}) => change.action === 'restored').length,
  }), [allRows, groups.length])

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return allRows.filter(({order, change}) => {
      if (orderFilter !== 'all' && order.order_id !== Number(orderFilter)) return false
      if (!matchesChangeFilter(change.action, changeFilter)) return false
      if (actorFilter !== 'all' && change.actor_type !== actorFilter) return false
      if (!query) return true
      return [
        order.order_number,
        order.cnpj,
        order.razao_social || '',
        change.product_name || '',
        change.ean || '',
        change.changed_by_name || '',
        change.reason_label || '',
        change.reason_text || '',
      ].join(' ').toLowerCase().includes(query)
    })
  }, [actorFilter, allRows, changeFilter, orderFilter, search])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className='fixed inset-0 z-[2147483000] flex items-center justify-center bg-slate-950/55 p-4 sm:p-6'
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className='flex max-h-[calc(100vh-3rem)] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl'>
        <div className='flex flex-none items-start justify-between gap-5 border-b border-slate-200 px-6 py-5'>
          <div className='flex min-w-0 items-start gap-3'>
            <span className='rounded-xl bg-violet-50 p-2.5 text-violet-700'>
              <History className='h-5 w-5' />
            </span>
            <div className='min-w-0'>
              <h2 className='text-xl font-bold text-slate-900'>{title}</h2>
              <p className='mt-1 text-sm text-slate-500'>{subtitle}</p>
            </div>
          </div>
          <button type='button' onClick={onClose} className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700' aria-label={'Fechar hist\u00f3rico'}>
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto'>
          <div className='grid gap-3 border-b border-slate-100 bg-slate-50/70 px-6 py-4 sm:grid-cols-2 xl:grid-cols-4'>
            <SummaryCard label={'Pedidos com hist\u00f3rico'} value={summary.orders} />
            <SummaryCard label='Quantidades alteradas' value={summary.quantities} tone='blue' />
            <SummaryCard label='Itens removidos' value={summary.removed} tone='red' />
            <SummaryCard label='Itens restaurados' value={summary.restored} tone='amber' />
          </div>

          <div className='flex flex-col gap-3 border-b border-slate-100 px-6 py-4 lg:flex-row lg:items-center'>
            <div className='relative min-w-0 flex-1'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={'Buscar pedido, produto, EAN, respons\u00e1vel ou motivo...'}
                className='w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
              />
            </div>
            {groups.length > 1 && (
              <select
                value={orderFilter}
                onChange={(event) => setOrderFilter(event.target.value)}
                className='max-w-[280px] rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-teal-500'
                aria-label='Filtrar por pedido'
              >
                <option value='all'>Todos os pedidos</option>
                {groups.map((order) => (
                  <option key={order.order_id} value={order.order_id}>{order.order_number} · {formatCnpj(order.cnpj)}</option>
                ))}
              </select>
            )}
            <select
              value={changeFilter}
              onChange={(event) => setChangeFilter(event.target.value as ChangeFilter)}
              className='rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-teal-500'
            >
              <option value='all'>Todos os eventos</option>
              <option value='quantity_changed'>Quantidades alteradas</option>
              <option value='removed'>Itens removidos</option>
              <option value='restored'>Itens restaurados</option>
              <option value='lifecycle'>{'Cria\u00e7\u00e3o e finaliza\u00e7\u00e3o'}</option>
            </select>
            <select
              value={actorFilter}
              onChange={(event) => setActorFilter(event.target.value as ActorFilter)}
              className='rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-teal-500'
            >
              <option value='all'>{'Todos os respons\u00e1veis'}</option>
              <option value='seller'>Alterado pelo seller</option>
              <option value='lojista'>Alterado pelo lojista</option>
            </select>
          </div>

          {loading ? (
            <div className='flex min-h-[360px] items-center justify-center text-sm text-slate-500'>
              <Loader2 className='mr-2 h-5 w-5 animate-spin text-teal-600' />
              {'Carregando hist\u00f3rico...'}
            </div>
          ) : error ? (
            <div className='flex min-h-[320px] items-center justify-center px-6 text-center text-sm font-medium text-red-700'>
              {error}
            </div>
          ) : visibleRows.length === 0 ? (
            <div className='flex min-h-[320px] flex-col items-center justify-center px-6 text-center'>
              <History className='mb-3 h-9 w-9 text-slate-300' />
              <p className='font-semibold text-slate-800'>{'Nenhuma altera\u00e7\u00e3o encontrada'}</p>
              <p className='mt-1 text-sm text-slate-500'>Tente modificar os filtros aplicados.</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full min-w-[1320px]'>
                <thead className='sticky top-0 z-10 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 shadow-[0_1px_0_#e2e8f0]'>
                  <tr>
                    <th className='px-5 py-3.5'>Pedido</th>
                    <th className='px-4 py-3.5'>Produto</th>
                    <th className='px-4 py-3.5'>{'Altera\u00e7\u00e3o'}</th>
                    <th className='px-4 py-3.5'>De</th>
                    <th className='px-4 py-3.5'>Para</th>
                    <th className='px-4 py-3.5'>{'Respons\u00e1vel'}</th>
                    <th className='px-4 py-3.5'>Data e hora</th>
                    <th className='px-5 py-3.5'>Motivo</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {visibleRows.map(({order, change}) => {
                    const meta = actionMeta(change.action)
                    const ActionIcon = meta.icon
                    return (
                      <tr key={`${order.order_id}-${change.audit_id}`} className='align-top transition hover:bg-slate-50/80'>
                        <td className='px-5 py-4'>
                          <p className='font-semibold text-slate-900'>{order.order_number}</p>
                          <p className='mt-1 font-mono text-[11px] text-slate-500'>{formatCnpj(order.cnpj)}</p>
                        </td>
                        <td className='max-w-[300px] px-4 py-4'>
                          <p className='font-medium text-slate-800'>{change.product_name || 'Evento do pedido'}</p>
                          {change.ean && <p className='mt-1 font-mono text-[11px] text-slate-500'>EAN {change.ean}</p>}
                        </td>
                        <td className='px-4 py-4'>
                          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${meta.className}`}>
                            <ActionIcon className='h-3.5 w-3.5' />
                            {meta.label}
                          </span>
                        </td>
                        <td className='px-4 py-4 text-sm font-medium text-slate-600'>{valueBefore(change)}</td>
                        <td className='px-4 py-4 text-sm font-semibold text-slate-900'>{valueAfter(change)}</td>
                        <td className='px-4 py-4'>
                          <p className='text-sm font-medium text-slate-800'>{change.changed_by_name || 'Usu\u00e1rio n\u00e3o identificado'}</p>
                          {change.actor_type && (
                            <span className='mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600'>
                              {change.actor_type === 'seller' ? 'Seller' : 'Lojista'}
                            </span>
                          )}
                        </td>
                        <td className='whitespace-nowrap px-4 py-4 text-sm text-slate-600'>
                          <span className='inline-flex items-center gap-1.5'><Clock3 className='h-3.5 w-3.5 text-slate-400' />{formatDate(change.changed_at)}</span>
                        </td>
                        <td className='max-w-[320px] px-5 py-4'>
                          <p className='text-sm font-semibold text-slate-800'>{change.reason_label || (change.action === 'created' || change.action === 'finalized' ? 'Evento autom\u00e1tico' : 'Motivo n\u00e3o informado')}</p>
                          {change.reason_text && <p className='mt-1 text-xs leading-5 text-slate-500'>{change.reason_text}</p>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='flex flex-none items-center justify-between border-t border-slate-200 bg-white px-6 py-4'>
          <p className='text-xs text-slate-500'>{visibleRows.length} de {allRows.length} evento(s) exibido(s)</p>
          <button type='button' onClick={onClose} className='rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800'>
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function SummaryCard({label, value, tone = 'slate'}: {label: string; value: number; tone?: 'slate' | 'blue' | 'red' | 'amber'}) {
  const colors = {
    slate: 'border-slate-200 bg-white text-slate-900',
    blue: 'border-blue-100 bg-blue-50/60 text-blue-800',
    red: 'border-red-100 bg-red-50/60 text-red-800',
    amber: 'border-amber-100 bg-amber-50/60 text-amber-800',
  }
  return (
    <div className={`rounded-xl border px-4 py-3 ${colors[tone]}`}>
      <p className='text-xs font-semibold uppercase tracking-wide opacity-70'>{label}</p>
      <p className='mt-1 text-2xl font-bold'>{value}</p>
    </div>
  )
}

